// src/utils/tenantSelectorAccess.ts

import { FEATURE_FLAGS } from "../config/featureFlags";

/**
 * TENANT_SELECTOR_ENABLED_USERS is a JSON object mapping each user's email to the
 * tenant option ids they may select in the dropdown, e.g.
 *   {"sally@x.com": ["*"], "user@catylex.com": ["<catylex-id>", "<bhi-id>"]}
 * where ["*"] grants every option. The map keys are the allow-list of who sees
 * the selector at all. The value must be empty or a JSON object — any other value
 * (including the old comma-separated format) throws, so a misconfigured build
 * fails loudly rather than silently mis-parsing.
 *
 * Exported (and pure) so it can be unit-tested independently of module load.
 *
 * @throws Error if `raw` is non-empty and not a JSON object of email -> id-list.
 */
export function parseTenantSelectorUsers(raw: string): Map<string, string[]> {
  const map = new Map<string, string[]>();
  if (!raw) return map;

  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    throw new Error(
      "TENANT_SELECTOR_ENABLED_USERS must be empty or a JSON object mapping " +
        'email -> list of tenant option ids, e.g. {"a@x.com": ["*"]}. ' +
        "The legacy comma-separated format is no longer supported."
    );
  }

  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    throw new Error(
      "TENANT_SELECTOR_ENABLED_USERS must be a JSON object (email -> list of tenant option ids)."
    );
  }

  for (const [email, ids] of Object.entries(obj)) {
    if (!Array.isArray(ids)) {
      throw new Error(
        `TENANT_SELECTOR_ENABLED_USERS["${email}"] must be a list of tenant option ids ` +
          '(use ["*"] to grant all).'
      );
    }
    map.set(
      email.trim().toLowerCase(),
      ids.map((id) => String(id))
    );
  }
  return map;
}

// Parsed once at module load for O(1) lookups.
const userTenantMap = parseTenantSelectorUsers(FEATURE_FLAGS.TENANT_SELECTOR_ENABLED_USERS);

/**
 * Whether the tenant selector should be available for a given user.
 *
 * - ENABLE_TENANT_SELECTOR must be true (master switch).
 * - If the config is empty → allow all users (backward-compat dev default).
 * - Otherwise only emails present in the config.
 */
export function isTenantSelectorEnabledForUser(userEmail: string | null | undefined): boolean {
  if (!FEATURE_FLAGS.ENABLE_TENANT_SELECTOR) {
    return false;
  }
  if (userTenantMap.size === 0) {
    return true;
  }
  if (!userEmail) {
    return false;
  }
  return userTenantMap.has(userEmail.toLowerCase());
}

/**
 * The tenant option ids the given user may choose from. Returns the sentinel
 * "all" when the user has no per-user restriction (empty config, or an explicit
 * ["*"] grant); returns [] for an unlisted user.
 */
export function allowedTenantIdsForUser(userEmail: string | null | undefined): "all" | string[] {
  if (userTenantMap.size === 0) {
    return "all";
  }
  if (!userEmail) {
    return [];
  }
  const ids = userTenantMap.get(userEmail.toLowerCase());
  if (!ids) {
    return [];
  }
  return ids.includes("*") ? "all" : ids;
}
